/**
 * Ad Tracking Plugin (Client-only)
 *
 * Initializes Google Ads, LinkedIn, Reddit, Twitter ad tracking providers
 * and injects as $adTracking.
 * Gated behind !IS_ON_PREM via extendPlugins() in nuxt.config.js.
 */

import { Context, Inject } from '@nuxt/types/app'
import { CookieConsentService } from '~/services/tracking/CookieConsentService'
import { AdTrackingService } from '~/services/tracking/AdTrackingService'
import { createLinkedInProvider } from '~/services/tracking/providers/linkedin'
import { createRedditProvider } from '~/services/tracking/providers/reddit'
import { createTwitterProvider } from '~/services/tracking/providers/twitter'

declare module 'vue/types/vue' {
  interface Vue {
    $adTracking: AdTrackingService
  }
}

declare module 'vuex/types/index' {
  // skipcq: JS-0387, JS-0356
  interface Store<S> {
    $adTracking: AdTrackingService
  }
}

declare module '@nuxt/types' {
  interface Context {
    $adTracking: AdTrackingService
  }
}

export default (_context: Context, inject: Inject): void => {
  const { $config, app } = _context

  const consentService = CookieConsentService.getInstance()
  const adTracking = new AdTrackingService(consentService)

  // Register LinkedIn provider
  const linkedInPartnerId = $config.adTrackingLinkedInPartnerId
  if (linkedInPartnerId) {
    adTracking.registerProvider(createLinkedInProvider({ partnerId: linkedInPartnerId }))
  }

  // Register Reddit provider
  const redditPixelId = $config.adTrackingRedditPixelId
  if (redditPixelId) {
    adTracking.registerProvider(createRedditProvider({ pixelId: redditPixelId }))
  }

  // Register Twitter provider
  const twitterPixelId = $config.adTrackingTwitterPixelId
  if (twitterPixelId) {
    adTracking.registerProvider(createTwitterProvider({ pixelId: twitterPixelId }))
  }

  // Initialize providers (consent is auto-granted in Phase 1)
  adTracking.initializeProviders()

  // Track page views on route changes
  if (app.router) {
    app.router.afterEach((to) => {
      adTracking.trackPageView(to.fullPath)
    })
  }

  inject('adTracking', adTracking)
}
